import { Check } from "lucide-react";
import { Reveal } from "./reveal";
import imgRefcom from "@assets/image_1776020263717.png";

const POINTS = [
  "Free, no-obligation site surveys and exact quotes",
  "Respectful engineers who protect your property",
  "Partnerships with tier-1 manufacturers",
  "Comprehensive warranties on all installations",
];

export function About() {
  return (
    <section id="about" className="relative overflow-hidden py-14 lg:py-20 bg-[hsl(var(--vx-paper))]" aria-labelledby="about-heading">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-[hsl(var(--vx-frost)/0.12)] blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-48 -left-32 w-[32rem] h-[32rem] rounded-full bg-[hsl(var(--vx-blue)/0.08)] blur-3xl" />

      <div className="relative container mx-auto max-w-7xl px-4">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Imagery */}
          <Reveal className="relative lg:col-span-6 pb-10 sm:pb-16">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] shadow-[0_40px_80px_-40px_hsl(var(--vx-ink)/0.55)] ring-1 ring-[hsl(var(--vx-ink)/0.06)]">
              <img
                src={`${import.meta.env.BASE_URL}images/about-vertexair-engineer.webp`}
                alt="VertexAir engineer standing beside a branded service van"
                width={1400}
                height={934}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-[30%_center]"
                data-testid="img-about-engineer"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--vx-ink)/0.75)] via-transparent to-transparent" />
              <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 text-xs font-semibold text-[hsl(var(--vx-ink))] shadow-sm">
                <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--vx-blue))]" />
                Homes &amp; businesses
              </div>
              <p className="absolute left-6 bottom-6 right-40 sm:right-56 vx-display text-white text-2xl sm:text-3xl font-bold">
                Our engineers.<br /><span className="text-[hsl(var(--vx-frost))]">Our vans.</span>
              </p>
            </div>

            <div className="absolute right-3 sm:-right-4 bottom-0 w-36 sm:w-52 rounded-[1.5rem] overflow-hidden ring-[6px] ring-[hsl(var(--vx-paper))] shadow-[0_30px_60px_-30px_hsl(var(--vx-ink)/0.6)] aspect-square rotate-[2deg] motion-safe:transition-transform motion-safe:duration-500 hover:rotate-0">
              <img
                src="/images/vertexair-team-vans.webp"
                alt="VertexAir team vans"
                width={800}
                height={800}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                data-testid="img-about-team"
              />
            </div>

            <div className="absolute -left-2 sm:-left-6 bottom-4 sm:bottom-10 rounded-2xl bg-white p-3 sm:p-4 shadow-[0_24px_48px_-24px_hsl(var(--vx-ink)/0.45)] ring-1 ring-[hsl(var(--vx-mist))]">
              <img src={imgRefcom} alt="REFCOM F-Gas Certificated" className="h-12 sm:h-16 w-auto" data-testid="img-about-refcom" />
            </div>
          </Reveal>

          {/* Copy */}
          <Reveal delay={0.1} className="lg:col-span-6">
            <p className="vx-eyebrow text-[hsl(var(--vx-blue))] mb-5 flex items-center gap-3">
              <span aria-hidden="true" className="h-px w-10 bg-[hsl(var(--vx-blue))]" />
              03 — Who we are
            </p>
            <h2 id="about-heading" className="vx-display text-4xl md:text-6xl font-bold text-[hsl(var(--vx-ink))] mb-8">
              The craft of climate control, <span className="text-[hsl(var(--vx-blue))]">done right.</span>
            </h2>
            <p className="text-lg text-slate-600 mb-5 leading-relaxed">
              At Vertex Climate, we don't just bolt units to walls. We engineer environments. Based in London, we understand the unique challenges of both historic residential properties and modern commercial spaces.
            </p>
            <p className="text-lg text-slate-600 mb-10 leading-relaxed border-l-2 border-[hsl(var(--vx-frost))] pl-5">
              Our approach is simple: immaculate workmanship, transparent communication, and an obsessive attention to detail. When you hire us, you're not just getting air conditioning — you're getting peace of mind.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {POINTS.map((p, i) => (
                <li
                  key={p}
                  data-testid={`text-about-point-${i}`}
                  className="vx-lift group relative flex flex-col gap-4 rounded-2xl bg-white p-5 ring-1 ring-[hsl(var(--vx-mist))]"
                >
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-9 h-9 rounded-xl bg-[hsl(var(--vx-ink))] text-[hsl(var(--vx-frost))]">
                      <Check className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <span aria-hidden="true" className="text-xs font-semibold tabular-nums text-slate-400">0{i + 1}</span>
                  </div>
                  <span className="font-semibold leading-snug text-[hsl(var(--vx-ink))]">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
